import { axiosClient } from '../../../library/Service/axiosClient';

const MENTORSHIP_BASE = `${process.env.REACT_APP_COMMON_API}/mentorship`;

export type MentorIdentity = {
    name: string | null;
    batchYear: number | null;
    campusId: number | null;
    designation: string | null;
    company: string | null;
};

export type MentorDashboardKpis = {
    menteesAssigned: number;
    maxMenteesPerMentor: number | null;
    sessionsCompletedInWindow: number;
    unreadMessages: string;
    cohortStartDate: string | null;
    cohortEndDate: string | null;
};

export type MentorDashboardMentee = {
    pairId: number | null;
    menteeName: string | null;
    program: string | null;
    batchYear: number | null;
};

export type MentorDashboardPayload = {
    mentor: MentorIdentity | null;
    kpis: MentorDashboardKpis;
    mentees: MentorDashboardMentee[];
};

export type MentorMenteeCard = {
    pairId: number | null;
    cohortId: number | null;
    menteeName: string | null;
    program: string | null;
    batchYear: number | null;
    campusId: number | null;
    tracks: { trackId: number | null; trackName: string | null }[];
    sessionCountInWindow: number;
    interactionCountInWindow: number;
    lastSessionDate: string | null;
    menteeUuid: string | null;
    sessionLinks: string[];
    goals: string | null;
    canLogOffline?: boolean;
    enabledInteractionModes?: string[];
    dodProgress?: {
        sessionCount: number;
        sessionMinimum: number;
        interactionCount: number;
        interactionMinimum: number;
        isComplete: boolean;
    };
};

export type MentorCreatePairSessionPayload = {
    occurredAt: string;
    title?: string;
    sessionLink?: string;
    durationMinutes?: number;
};

export type MentorCreatePairInteractionPayload = {
    occurredAt: string;
    mode: string;
    notes?: string;
};

export type MentorMenteesPayload = {
    mentees: MentorMenteeCard[];
};

export type MentorAvailabilitySlot = {
    dayOfWeek: number;
    startMinute: number;
};

export type MentorBookedSlot = {
    bookingId: number | null;
    pairId: number | null;
    bookedDate: string | null;
    startMinute: number | null;
    durationMinutes: number | null;
    status: string | null;
};

export type MentorUpcomingBooking = {
    id: number;
    cohortId: number | null;
    pairId: number | null;
    mentorAlumniId: string | null;
    bookedDate: string | null;
    startMinute: number | null;
    durationMinutes: number | null;
    topic: string;
    agenda: string | null;
    mode: string | null;
    sessionLink: string | null;
    bookedByRole: string | null;
    bookedById: string | null;
    status: string | null;
};

export type MentorAvailabilityPayload = {
    slotDurationMinutes: number;
    slots: MentorAvailabilitySlot[];
    bookedSlots?: MentorBookedSlot[];
    upcomingBookings?: MentorUpcomingBooking[];
};

export type MentorPastSessionRow = {
    id: number;
    occurredAt: string | null;
    pairId: number | null;
    menteeName: string | null;
    durationMinutes: number | null;
    title: string | null;
    sessionLink: string | null;
    loggedByRole: string | null;
};

export type MentorPastSessionsPayload = {
    sessions: MentorPastSessionRow[];
    pagination: {
        page: number;
        pageSize: number;
        total: number;
        totalPages: number;
    };
    upcomingBookings?: MentorUpcomingBooking[];
};

export type MentorProfileIdentity = {
    name: string | null;
    photo: string | null;
    program: string | null;
    batchYear: number | null;
    campusId: number | null;
    company: string | null;
    designation: string | null;
    experienceYears: number | null;
};

export type MentorProfileTrackOption = {
    id: number;
    name: string | null;
};

export type MentorProfileChecklistItem = {
    id: string;
    done: boolean;
    pendingCopy?: string;
};

export type MentorProfileChecklist = {
    doneCount: number;
    total: number;
    progressLabel: string;
    items: MentorProfileChecklistItem[];
};

export type MentorProfilePayload = {
    identity: MentorProfileIdentity;
    bio: string | null;
    currentCompany: string | null;
    designation: string | null;
    location: string | null;
    linkedinUrl: string | null;
    specificAreas: string | null;
    notifyNewMessage: boolean;
    notifyBooking: boolean;
    notifySessionReminder: boolean;
    notifyMonthlyDigest: boolean;
    trackCatalog: MentorProfileTrackOption[];
    poolTrackIds: number[];
    storedPreferredTrackIds: number[] | null;
    preferredTrackIds: number[];
    matchingHelper: string;
    availabilitySlotCount: number;
    checklist: MentorProfileChecklist;
};

export type MentorProfilePutBody = {
    bio: string;
    currentCompany: string;
    designation: string;
    location: string;
    linkedinUrl: string;
    specificAreas: string;
    preferredTrackIds: number[];
    notifyNewMessage: boolean;
    notifyBooking: boolean;
    notifySessionReminder: boolean;
    notifyMonthlyDigest: boolean;
};

const unwrap = <T>(response: { data?: { data?: T } & T }): T => {
    return (response?.data?.data ?? response?.data) as T;
};

class MentorPortalService {
    static async getDashboard(): Promise<MentorDashboardPayload> {
        const response = await axiosClient().get(`${MENTORSHIP_BASE}/mentor/dashboard`);
        return unwrap<MentorDashboardPayload>(response);
    }

    static async getMentees(): Promise<MentorMenteesPayload> {
        const response = await axiosClient().get(`${MENTORSHIP_BASE}/mentor/mentees`);
        return unwrap<MentorMenteesPayload>(response);
    }

    static async getAvailability(): Promise<MentorAvailabilityPayload> {
        const response = await axiosClient().get(`${MENTORSHIP_BASE}/mentor/availability`);
        return unwrap<MentorAvailabilityPayload>(response);
    }

    static async putAvailability(payload: MentorAvailabilityPayload): Promise<MentorAvailabilityPayload> {
        const response = await axiosClient().put(`${MENTORSHIP_BASE}/mentor/availability`, payload);
        return unwrap<MentorAvailabilityPayload>(response);
    }

    static async listSessions(query: { page?: number; pageSize?: number } = {}): Promise<MentorPastSessionsPayload> {
        const response = await axiosClient().get(`${MENTORSHIP_BASE}/mentor/sessions`, {
            params: {
                page: query.page,
                pageSize: query.pageSize,
            },
        });
        const data = unwrap<MentorPastSessionsPayload>(response);
        const pagination = data.pagination || { page: 1, pageSize: 25, total: 0, totalPages: 0 };
        return {
            sessions: data.sessions || [],
            pagination: {
                page: pagination.page ?? 1,
                pageSize: pagination.pageSize ?? 25,
                total: pagination.total ?? 0,
                totalPages: pagination.totalPages ?? 0,
            },
            upcomingBookings: data.upcomingBookings || [],
        };
    }

    static async getProfile(): Promise<MentorProfilePayload> {
        const response = await axiosClient().get(`${MENTORSHIP_BASE}/mentor/profile`);
        return unwrap<MentorProfilePayload>(response);
    }

    static async putProfile(payload: MentorProfilePutBody): Promise<MentorProfilePayload> {
        const response = await axiosClient().put(`${MENTORSHIP_BASE}/mentor/profile`, payload);
        return unwrap<MentorProfilePayload>(response);
    }

    /** Truncated dashboard widget rows — no uuid/tracks/campus name (004 gap). */
    static cardsFromDashboardMentees(rows: MentorDashboardMentee[] | undefined): MentorMenteeCard[] {
        return (rows || []).map((row) => ({
            pairId: row.pairId,
            cohortId: null,
            menteeName: row.menteeName,
            program: row.program,
            batchYear: row.batchYear,
            campusId: null,
            tracks: [],
            sessionCountInWindow: 0,
            interactionCountInWindow: 0,
            lastSessionDate: null,
            menteeUuid: null,
            sessionLinks: [],
            goals: null,
            canLogOffline: false,
            enabledInteractionModes: [],
        }));
    }

    static errorMessage(error: { message?: string; data?: { message?: string } } | unknown): string {
        const err = error as { message?: string; data?: { message?: string } };
        return err?.message || err?.data?.message || 'An error occurred while processing your request.';
    }

    static async createPairSession(
        pairId: number,
        payload: MentorCreatePairSessionPayload
    ): Promise<unknown> {
        const response = await axiosClient().post(
            `${MENTORSHIP_BASE}/mentor/pairs/${pairId}/sessions`,
            payload
        );
        return unwrap(response);
    }

    static async createPairInteraction(
        pairId: number,
        payload: MentorCreatePairInteractionPayload
    ): Promise<unknown> {
        const response = await axiosClient().post(
            `${MENTORSHIP_BASE}/mentor/pairs/${pairId}/interactions`,
            payload
        );
        return unwrap(response);
    }

    /** Existing student profile path (StudentList / routes `/student/:id/profile`). Not a Mentorship profile page. */
    static studentProfileHref(uuid: string | null | undefined): string | null {
        const id = uuid ? String(uuid).trim() : '';
        return id ? `/student/${id}/profile` : null;
    }

    /** Mentor Messages page; optional ?mentee= uuid pre-selects that conversation (DEC-P14-022). */
    static messagesHref(menteeUuid?: string | null): string {
        const base = '/alumni/mentorship/messages';
        const id = menteeUuid ? String(menteeUuid).trim() : '';
        return id ? `${base}?mentee=${encodeURIComponent(id)}` : base;
    }

    /** Dashboard KPIs from GET /mentor/dashboard; widget rows prefer GET /mentor/mentees (uuid/tracks). */
    static async loadDashboardView(): Promise<{
        dashboard: MentorDashboardPayload;
        menteeCards: MentorMenteeCard[];
    }> {
        const dashboard = await MentorPortalService.getDashboard();
        let menteeCards: MentorMenteeCard[] = [];
        try {
            const extra = await MentorPortalService.getMentees();
            menteeCards = extra.mentees || [];
        } catch {
            menteeCards = [];
        }
        if (menteeCards.length === 0) {
            menteeCards = MentorPortalService.cardsFromDashboardMentees(dashboard.mentees);
        }
        return { dashboard, menteeCards };
    }
}

export default MentorPortalService;
